import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import SearchAddressComboBox from "@shared/components/application/form/SearchAddressComboBox/SearchAddressComboBox.web";
import Collapsible from "@shared/components/ui-kit/disclosure/Collapsible/Collapsible.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import useMountEffect from "@shared/hooks/useMountEffect/useMountEffect";
import { PlaceAddress } from "@shared/lib/google";
import { useTranslation } from "@shared/utils/internationalization";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import FormRow from "@ui-kit/form/FormRow/FormRow.web";
import Select from "@ui-kit/form/Select/Select.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { startTransition, useEffect, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import SearchAddressFormInput from "../SearchAddressFormInput/SearchAddressFormInput.web";
import { SearchAddressFormProps } from "./SearchAddressForm.props";
import { useSearchAddressForm } from "./useSearchAddressForm";

// Reuse this component for enhanced info, credit card form (on the /account route), and shipping/billing addresses on all checkouts
export default function SearchAddressForm(props: SearchAddressFormProps) {
  const { t } = useTranslation("common");
  const {
    formId,
    form,
    isRequired,
    updateAddressCallback,
    titleProps,
    initialValuesData,
    label = t("Search address"),
    allowedCountries,
    searchAriaLabel,
  } = props;
  const {
    streetNameId,
    unitNumberId,
    countryId,
    regionId,
    cityId,
    postalCodeId,
    selectedCountryItem,
    selectedRegionItem,
    permittedCountries,
    updateCountry,
    updateRegion,
    updateInput,
    initializeForm,
    validateAllowedCountry,
    getCountryItem,
  } = useSearchAddressForm(
    form,
    formId,
    updateAddressCallback,
    allowedCountries
  );

  const [displayIndividualFields, setDisplayIndividualFields] = useState(false);
  const [isManualEntryButtonVisible, setIsManualEntryButtonVisible] =
    useState<boolean>(!displayIndividualFields);

  const autocompleteScrollTargetRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToTarget, setShouldScrollToTarget] = useState(false);

  // Effect to initialize form with initial values if provided
  useMountEffect(() => {
    if (
      initialValuesData &&
      Object.values(initialValuesData).some((value) => value !== null)
    ) {
      initializeForm(initialValuesData);

      // If we have initial values, immediately show the fields
      startTransition(() => {
        setDisplayIndividualFields(true);
        setIsManualEntryButtonVisible(false);
      });
    }
  });

  // Subscribe to form state changes to detect when fields are filled
  useEffect(() => {
    // Check if form has any values - more efficient than using useWatch
    const checkFormHasValues = () => {
      const values = form.getValues();
      return Boolean(
        values[streetNameId] ||
          values[unitNumberId] ||
          values[countryId] ||
          values[regionId] ||
          values[cityId] ||
          values[postalCodeId]
      );
    };

    const subscription = form.watch(() => {
      if (!displayIndividualFields && checkFormHasValues()) {
        startTransition(() => {
          setDisplayIndividualFields(true);
          setIsManualEntryButtonVisible(false);
        });
      }
    });

    // Check immediately in case form already has values
    if (!displayIndividualFields && checkFormHasValues()) {
      startTransition(() => {
        setDisplayIndividualFields(true);
        setIsManualEntryButtonVisible(false);
      });
    }

    return () => subscription.unsubscribe();
  }, [form, displayIndividualFields]);

  // New effect: Check for form errors and display fields if errors exist
  useEffect(() => {
    // Only proceed if fields are not already displayed
    if (!displayIndividualFields) {
      // Check if any form fields have errors
      const hasErrors = Boolean(
        form.formState.errors[streetNameId] ||
          form.formState.errors[unitNumberId] ||
          form.formState.errors[countryId] ||
          form.formState.errors[regionId] ||
          form.formState.errors[cityId] ||
          form.formState.errors[postalCodeId]
      );

      // If there are errors, display the fields
      if (hasErrors) {
        startTransition(() => {
          setDisplayIndividualFields(true);
          setIsManualEntryButtonVisible(false);
        });
      }
    }
  }, [form.formState.errors, displayIndividualFields]);

  // Effect to handle scrolling when fields are displayed and shouldScrollToTarget is true
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (
      displayIndividualFields &&
      shouldScrollToTarget &&
      autocompleteScrollTargetRef.current
    ) {
      // Use a small timeout to ensure the Collapsible animation has started
      const timeoutId = setTimeout(() => {
        autocompleteScrollTargetRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setShouldScrollToTarget(false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [displayIndividualFields, shouldScrollToTarget]);

  const onAddressComboboxSelectedItemChange = (address: PlaceAddress) => {
    // Ignore react queue and update the state immediately, ignoring renders caused by state updates from
    // the logic after this code block
    startTransition(() => {
      setShouldScrollToTarget(true);
    });

    if (address.countryShortCode) {
      const countryItem = getCountryItem(address.country);

      if (countryItem) {
        // Set the country form value and update the dropdown
        updateCountry(countryItem);

        // Update region if we have a region code
        if (address.regionShortCode && countryItem.regions) {
          const regionItem = countryItem.regions.find(
            (region) => region.shortCode === address.regionShortCode
          );

          if (regionItem) {
            // Set the region form value and update the dropdown
            updateRegion(regionItem);
          }
        }
      }
    }

    updateInput(cityId, address.city);
    updateInput(postalCodeId, address.postalCode);
    updateInput(streetNameId, address.streetAddress);
    updateInput(unitNumberId, address.unitNumber);
  };

  return (
    <Flex direction="column" gap="lg">
      <Box>
        <FormLabel htmlFor={`${formId}-search-address`}>
          <Text fontSize="sm" color="font.dark" {...titleProps}>
            {label}
          </Text>
        </FormLabel>

        <Flex direction="column" gap="2xs">
          <SearchAddressComboBox
            id={`${formId}-search-address`}
            ariaLabel={searchAriaLabel}
            placeholder={t("Search address")}
            onSelectedItemChange={onAddressComboboxSelectedItemChange}
          />

          <Collapsible in={isManualEntryButtonVisible}>
            <Box>
              <Button
                variant="link"
                size="sm"
                paddingLeft="none"
                onClick={() => {
                  startTransition(() => {
                    setDisplayIndividualFields(true);
                    setIsManualEntryButtonVisible(false);
                    setTimeout(() => {
                      setShouldScrollToTarget(true);
                    }, 300);
                  });
                }}
                data-testid={`${formId}-manual-entry-button`}
                color="font.dark"
              >
                {t("Enter address manually")}
              </Button>
            </Box>
          </Collapsible>
        </Flex>
      </Box>
      <Box
        style={{
          // Remove spacing added by the `gap` on the containing flexbox.
          // Because of the Collapse component, it displays a 20px gap.
          // Use `style` for fast performance
          marginTop: displayIndividualFields ? "0px" : "-20px",
        }}
      >
        <FormProvider {...form}>
          <Collapsible
            in={displayIndividualFields}
            transition={{
              enter: { duration: 0.1 },
              exit: { duration: 0.1 },
            }}
          >
            <Box
              margin="none" // Allow outline of input elements to be visible
              data-testid={formId}
            >
              <Flex direction="column" gap="lg">
                {/* Street Name */}
                <SearchAddressFormInput
                  inputId={streetNameId}
                  title={t("Street Name")}
                  placeholder={t("Street Name")}
                  required={isRequired}
                  updateInput={updateInput}
                  titleProps={titleProps}
                />
                {/* Optional Unit Number */}
                <SearchAddressFormInput
                  inputId={unitNumberId}
                  title={t("Apt, Suite, Unit (optional)")}
                  placeholder={t("Apt, Suite, Unit")}
                  required={false}
                  updateInput={updateInput}
                  titleProps={titleProps}
                />

                {/* City */}
                <SearchAddressFormInput
                  inputId={cityId}
                  title={t("City")}
                  placeholder={t("City")}
                  required={isRequired}
                  updateInput={updateInput}
                  titleProps={titleProps}
                />

                {/* Country Select */}
                <Flex
                  direction="column"
                  gap="2xs"
                  ref={autocompleteScrollTargetRef}
                >
                  <FormRow>
                    <FormControl
                      isInvalid={Boolean(
                        form.formState.errors?.[countryId]?.message
                      )}
                    >
                      <FormLabel htmlFor={countryId}>
                        <Text fontSize="sm" color="font.dark" {...titleProps}>
                          {t("Country")}
                        </Text>
                      </FormLabel>
                      <Controller
                        name={countryId}
                        control={form.control}
                        defaultValue=""
                        rules={{
                          required: isRequired
                            ? t("This field is required.")
                            : false,
                          validate: validateAllowedCountry,
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            id={countryId}
                            placeholder={t("Select Country")}
                            items={permittedCountries}
                            selectedItem={selectedCountryItem}
                            isInvalid={Boolean(
                              form.formState.errors?.[countryId]?.message
                            )}
                            onSelectedItemChange={({ selectedItem }) => {
                              updateCountry(selectedItem);
                            }}
                          />
                        )}
                      />
                      <FormErrorMessage>
                        {String(form.formState.errors?.[countryId]?.message)}
                      </FormErrorMessage>
                    </FormControl>
                  </FormRow>
                </Flex>

                {/* Province / State Select */}
                <Flex direction="column" gap="2xs">
                  <FormRow>
                    <FormControl
                      isInvalid={Boolean(
                        form.formState.errors?.[regionId]?.message
                      )}
                    >
                      <FormLabel htmlFor={regionId}>
                        <Text fontSize="sm" color="font.dark" {...titleProps}>
                          {t("Region")}
                        </Text>
                      </FormLabel>
                      <Controller
                        name={regionId}
                        control={form.control}
                        defaultValue=""
                        rules={{
                          required: isRequired
                            ? t("This field is required.")
                            : false,
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            id={regionId}
                            placeholder={t("Select Region")}
                            items={selectedCountryItem?.regions || []}
                            selectedItem={selectedRegionItem}
                            isInvalid={Boolean(
                              form.formState.errors?.[regionId]?.message
                            )}
                            disabled={!selectedCountryItem}
                            onSelectedItemChange={({ selectedItem }) => {
                              updateRegion(selectedItem);
                            }}
                          />
                        )}
                      />
                      <FormErrorMessage>
                        {String(form.formState.errors?.[regionId]?.message)}
                      </FormErrorMessage>
                    </FormControl>
                  </FormRow>
                </Flex>

                {/* Zip Code / Postal Code */}
                <SearchAddressFormInput
                  inputId={postalCodeId}
                  title={t("Postal/Zip Code")}
                  placeholder={t("Postal/Zip Code")}
                  required={isRequired}
                  updateInput={updateInput}
                  titleProps={titleProps}
                />
              </Flex>
            </Box>
          </Collapsible>
        </FormProvider>
      </Box>
    </Flex>
  );
}
